import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Star, Send, CheckCircle2, Loader2, ExternalLink, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicReview = () => {
  const { orgId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [subscriptionActive, setSubscriptionActive] = useState(true);

  React.useEffect(() => {
    fetchOrganization();
  }, [orgId]);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/public/organization/${orgId}`);
      setOrganization(response.data);
      setSubscriptionActive(response.data.subscription_active !== false);
    } catch (error) {
      console.error("Error fetching organization:", error);
      toast.error("Établissement non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }

    setSubmitting(true);

    try {
      // Send review to backend
      await axios.post(`${API}/reviews/${orgId}`, {
        rating,
        comment: comment.trim() || null,
        author_name: rating >= 4 ? "Avis positif" : "Avis négatif",
        author_email: null
      });

      // If rating >= 4, redirect to Google
      // Generate URL from Place ID if google_business_url is not available
      const googleUrl = organization?.google_business_url || 
        (organization?.google_place_id ? `https://search.google.com/local/writereview?placeid=${organization.google_place_id}` : null);
      
      if (rating >= 4 && googleUrl) {
        setRedirecting(true);
        toast.success("Merci ! Redirection vers Google...");
        setTimeout(() => {
          window.location.href = googleUrl;
        }, 1500);
      } else {
        setSubmitted(true);
        toast.success("Merci pour votre retour !");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingText = (r) => {
    if (r === 5) return "Excellent !";
    if (r === 4) return "Très bien !";
    if (r === 3) return "Correct";
    if (r === 2) return "Peut mieux faire";
    if (r === 1) return "Décevant";
    return "";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Établissement non trouvé</h1>
          <p className="text-slate-500">Le lien que vous avez utilisé n'est pas valide.</p>
        </div>
      </div>
    );
  }

  // Show message when subscription is not active
  if (!subscriptionActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-amber-50 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Service temporairement indisponible
          </h1>
          <p className="text-slate-600 mb-4">
            La collecte d'avis pour <strong>{organization.name}</strong> est actuellement suspendue.
          </p>
          <p className="text-sm text-slate-400">
            Si vous êtes le propriétaire, veuillez réactiver votre abonnement pour réactiver ce service.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Merci pour votre retour !
          </h1>
          <p className="text-slate-500">
            Votre avis nous aide à améliorer nos services.
          </p>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExternalLink className="w-10 h-10 text-violet-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Merci !
          </h1>
          <p className="text-slate-500 mb-4">
            Redirection vers Google...
          </p>
          <Loader2 className="w-6 h-6 animate-spin text-violet-600 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4" data-testid="public-review-page">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          {/* Header */}
          <div className="text-center mb-8">
            {organization.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.name}
                className="w-20 h-20 object-cover rounded-2xl mx-auto mb-4 border border-slate-100"
              />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">
                  {organization.name[0].toUpperCase()}
                </span>
              </div>
            )}
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-1">
              {organization.name}
            </h1>
            <p className="text-slate-500 capitalize text-sm">{organization.business_type}</p>
          </div>

          {/* Rating */}
          <div className="text-center mb-6">
            <p className="text-sm font-medium text-slate-700 mb-4">
              Comment évaluez-vous votre expérience ?
            </p>
            <div className="flex justify-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-12 h-12 transition-colors ${
                      star <= (hoverRating || rating)
                        ? "text-amber-400 fill-amber-400"
                        : "text-slate-200"
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className={`text-sm font-medium ${rating >= 4 ? "text-emerald-600" : "text-slate-500"}`}>
                {getRatingText(rating)}
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Comment - Only show when rating < 4 */}
            {rating > 0 && rating < 4 && (
              <div className="space-y-2 animate-fade-in">
                <label htmlFor="comment" className="text-sm text-slate-600">
                  Dites-nous en plus <span className="text-slate-400">(optionnel)</span>
                </label>
                <Textarea
                  id="comment"
                  name="comment"
                  placeholder="Qu'est-ce qui pourrait être amélioré ?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="rounded-xl bg-slate-50 border-slate-200 focus:bg-white resize-none"
                  data-testid="review-comment"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || rating === 0}
              className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90 disabled:opacity-50"
              data-testid="submit-review-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {rating >= 4 ? "Continuer vers Google" : "Envoyer"}
                </>
              )}
            </Button>
          </form>

          {/* Info about redirect */}
          {rating >= 4 && (
            <div className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-xs text-emerald-700 text-center">
                ✨ Super ! Vous allez être redirigé vers Google pour publier votre avis.
              </p>
            </div>
          )}
          
          {rating > 0 && rating < 4 && (
            <p className="text-[10px] text-slate-300 text-center mt-4">
              Cet avis ne sera pas publié sur Google et sera transmis uniquement à l'établissement.
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Propulsé par <span className="font-medium">ResponsIA</span>
        </p>
      </div>
    </div>
  );
};

export default PublicReview;
